import { fetchPartnerRequestById } from 'legacy/lut/services/partner/fetchPartnerRequestById'
import {
  deleteBasicEditPartnerRequest,
  deleteLegalEditPartnerRequest,
  updateBasicPartnerRequestStatus,
  updateLegalPartnerRequestStatus,
} from 'legacy/lut/services/partner/partnerRequest'
import { memo, useCallback, useMemo, useState } from 'react'

import { PartnerFormMenuItems, PartnerWorkflowStatus } from 'clickNgo/entities/partner/configs'
import { IPartner } from 'clickNgo/entities/partner/models'
import { useCheckLegalApproverAccess } from 'clickNgo/hooks/access'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { useGetFormDataField } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { Tooltip } from 'core-components/library/tooltip'
import { ConfirmDialog } from 'shared/components/confirmDialog'

import { PartnerEditorFormKeys, PartnerEditorFormSettings } from '../../configs'
import { useEditMenuItems } from '../../hooks/useEditMenuItems'
import { useReadOnlyMenuItem } from '../../hooks/useReadOnlyMenuItem'
import { IPartnerEditorData } from '../../models'
import { partnerEditorTranslation } from '../../translation'

import { IPartnerWorkflowStatusPanelProps } from './models'

import styles from './PartnerWorkflowStatusPanel.module.scss'

export const PartnerWorkflowStatusPanel = memo<IPartnerWorkflowStatusPanelProps>(({ partner }) => {
  /** ------------------------- Variables -------------------------- */
  const { isReadOnly } = useReadOnlyMenuItem()
  const isLegalApprover = useCheckLegalApproverAccess()
  const { currentMenuItem } = useEditMenuItems(partner)
  const isBasicRequest = currentMenuItem === PartnerFormMenuItems.Basic

  const entryId = isBasicRequest ? partner.entryId406 : partner.entryId502
  const workflowStatus = isBasicRequest ? partner.workflowStatus : partner.legalWorkflowStatus
  const partnerId = partner.partnerId

  const [, setIsLoading] = useGetFormDataField(
    PartnerEditorFormKeys.Setting,
    PartnerEditorFormSettings.IsLoading
  )

  const [actualData, updateActualData] = useGetFormDataField<IPartnerEditorData>(
    PartnerEditorFormKeys.Setting,
    PartnerEditorFormSettings.ActualData
  )

  const [isApproveRequestConfirmDialogVisible, setIsApproveRequestConfirmDialogVisible] =
    useState(false)
  const [isRejectRequestConfirmDialogVisible, setIsRejectRequestConfirmDialogVisible] =
    useState(false)
  const [isRemoveRequestConfirmDialogVisible, setIsRemoveRequestConfirmDialogVisible] =
    useState(false)

  const confirmDialogText = useMemo(() => {
    switch (true) {
      case isApproveRequestConfirmDialogVisible:
        return partnerEditorTranslation.dialog.approveRequest
      case isRejectRequestConfirmDialogVisible:
        return partnerEditorTranslation.dialog.rejectRequest
      default:
        return partnerEditorTranslation.dialog.removeRequest
    }
  }, [isApproveRequestConfirmDialogVisible, isRejectRequestConfirmDialogVisible])

  const isResolved = [
    PartnerWorkflowStatus.APPROVED,
    PartnerWorkflowStatus.DONE,
    PartnerWorkflowStatus.REJECTED,
  ].includes(workflowStatus)
  const isDraft = workflowStatus === PartnerWorkflowStatus.DRAFT
  const isAwaiting = workflowStatus === PartnerWorkflowStatus.AWAITING
  const isApproveButtonVisible = isLegalApprover && isAwaiting

  /** ------------------------- Handlers -------------------------- */
  const approveRequest = useCallback(
    async (comment: string = '') => {
      setIsLoading(true)

      let result

      if (isBasicRequest) {
        result = updateBasicPartnerRequestStatus({
          EntryId: entryId,
          WorkflowStatus: PartnerWorkflowStatus.APPROVED,
          ProfileApproverComment: comment,
        })
      } else {
        result = updateLegalPartnerRequestStatus({
          EntryId: entryId,
          LegalWorkflowStatus: PartnerWorkflowStatus.APPROVED,
          LegalApproverComment: comment,
        })
      }

      result
        .then((result) => {
          updateActualData({
            ...actualData,
            partner: { ...actualData.partner, ...result },
          })
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [actualData, entryId, isBasicRequest, setIsLoading, updateActualData]
  )

  const rejectRequest = useCallback(
    async (comment: string = '') => {
      setIsLoading(true)

      let result

      if (isBasicRequest) {
        result = updateBasicPartnerRequestStatus({
          EntryId: entryId,
          WorkflowStatus: PartnerWorkflowStatus.REJECTED,
          ProfileApproverComment: comment,
        })
      } else {
        result = updateLegalPartnerRequestStatus({
          EntryId: entryId,
          LegalWorkflowStatus: PartnerWorkflowStatus.REJECTED,
          LegalApproverComment: comment,
        })
      }

      const rejectedActualData: Partial<IPartner> = isBasicRequest
        ? {
            entryId406: 0,
            workflowStatus: PartnerWorkflowStatus.NONE,
            approverComment: '',
            modifiedValues: [],
          }
        : {
            entryId502: 0,
            legalWorkflowStatus: PartnerWorkflowStatus.NONE,
            legalApproverComment: '',
            legalModifiedValues: [],
          }

      result
        .then(() => {
          updateActualData({
            ...actualData,
            partner: { ...actualData.partner, ...rejectedActualData },
          })
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [actualData, entryId, isBasicRequest, setIsLoading, updateActualData]
  )

  const removedRequest = useCallback(async () => {
    setIsLoading(true)

    const removeRequestResult = isBasicRequest
      ? await deleteBasicEditPartnerRequest({ entryId406: entryId, workflowStatus })
      : await deleteLegalEditPartnerRequest({
          entryId502: entryId,
          legalWorkflowStatus: workflowStatus,
        })

    if (removeRequestResult) {
      const cacheKey = `fetchPartnerRequestById-removedRequest-${partnerId}`
      const partner = await fetchPartnerRequestById(partnerId, { cacheKey, isForce: true })
      updateActualData({ ...actualData, partner })
    }

    setIsLoading(false)
  }, [
    setIsLoading,
    isBasicRequest,
    entryId,
    workflowStatus,
    partnerId,
    updateActualData,
    actualData,
  ])

  const handleOnRequestApprove = useCallback(() => {
    setIsApproveRequestConfirmDialogVisible(true)
  }, [])
  const handleOnRequestReject = useCallback(() => {
    setIsRejectRequestConfirmDialogVisible(true)
  }, [])
  const handleOnRequestRemove = useCallback(() => {
    setIsRemoveRequestConfirmDialogVisible(true)
  }, [])

  const handleOnConfirm = useCallback(
    async (comment?: string) => {
      if (isApproveRequestConfirmDialogVisible) {
        setIsApproveRequestConfirmDialogVisible(false)
        approveRequest(comment)
      }
      if (isRejectRequestConfirmDialogVisible) {
        setIsRejectRequestConfirmDialogVisible(false)
        rejectRequest(comment)
      }
      if (isRemoveRequestConfirmDialogVisible) {
        setIsRemoveRequestConfirmDialogVisible(false)
        removedRequest()
      }
    },
    [
      approveRequest,
      isApproveRequestConfirmDialogVisible,
      isRejectRequestConfirmDialogVisible,
      isRemoveRequestConfirmDialogVisible,
      rejectRequest,
      removedRequest,
    ]
  )

  const handleOnReject = useCallback(() => {
    setIsApproveRequestConfirmDialogVisible(false)
    setIsRejectRequestConfirmDialogVisible(false)
    setIsRemoveRequestConfirmDialogVisible(false)
  }, [])

  const renderApproveButtonTooltip = useCallback(
    () => <Translation text={partnerEditorTranslation.workflowStatus.button.approve} span />,
    []
  )

  const renderRejectButtonTooltip = useCallback(
    () => <Translation text={partnerEditorTranslation.workflowStatus.button.reject} span />,
    []
  )

  const renderRemovedButtonTooltip = useCallback(
    () => <Translation text={partnerEditorTranslation.workflowStatus.button.remove} span />,
    []
  )

  if (workflowStatus) {
    return (
      <div className={styles.container}>
        <div className={styles.status}>
          <div className={styles.statusLabel}>
            <Translation text={partnerEditorTranslation.workflowStatus.label} span />
          </div>
          <div className={styles.statusValue}>{workflowStatus}</div>
        </div>
        <div className={styles.actions}>
          <div className={styles.workflowActions}>
            <RenderWhen condition={isApproveButtonVisible && !isReadOnly}>
              <Tooltip tipContent={renderApproveButtonTooltip}>
                <Button
                  onClick={handleOnRequestApprove}
                  color="success"
                  variant="text"
                  icon="kbase kbase-like"
                  className={styles.button}
                >
                  <Translation text={partnerEditorTranslation.workflowStatus.button.approve} span />
                </Button>
              </Tooltip>
            </RenderWhen>
            <RenderWhen condition={isApproveButtonVisible && !isReadOnly}>
              <Tooltip tipContent={renderRejectButtonTooltip}>
                <Button
                  onClick={handleOnRequestReject}
                  color="warning"
                  variant="text"
                  className={styles.button}
                  icon="kbase kbase-dislike"
                >
                  <Translation text={partnerEditorTranslation.workflowStatus.button.reject} span />
                </Button>
              </Tooltip>
            </RenderWhen>
          </div>
          <RenderWhen condition={!(isResolved || isReadOnly || isDraft)}>
            <Tooltip tipContent={renderRemovedButtonTooltip}>
              <Button
                onClick={handleOnRequestRemove}
                color="danger"
                variant="text"
                icon="kbase kbase-delete-alt"
              >
                <Translation text={partnerEditorTranslation.workflowStatus.button.remove} span />
              </Button>
            </Tooltip>
          </RenderWhen>
        </div>
        <ConfirmDialog
          content={confirmDialogText.content}
          confirmBtnText={confirmDialogText.confirmBtn}
          onConfirm={handleOnConfirm}
          isVisible={
            isApproveRequestConfirmDialogVisible ||
            isRemoveRequestConfirmDialogVisible ||
            isRejectRequestConfirmDialogVisible
          }
          title={confirmDialogText.title}
          onReject={handleOnReject}
          rejectBtnText={confirmDialogText.rejectBtn}
          hasComment={isApproveRequestConfirmDialogVisible || isRejectRequestConfirmDialogVisible}
          size="xs"
        />
      </div>
    )
  }

  return null
})
